import { useState, useCallback, useRef, useEffect } from 'react'
import { Location, LocationType } from '../SearchLocation.types'
import { useDropdownState } from './useDropdownState'
import {
  useLocationSelection,
  type LocationResolver,
} from './useLocationSelection'
import { useMapAdapter } from '../adapters/useMapAdapter'
import { type LocationValidator } from '../core/locationValidator'
import { defaultSearchConfig } from '../core/searchConfig'
import type { MapAdapterOptions } from '../adapters/types'

/**
 * Search function type - returns locations matching query
 */
export type LocationSearchFunction = (
  query: string,
  types: LocationType[]
) => Promise<Location[]>

/**
 * Options for useLocationSearch hook
 */
export interface UseLocationSearchOptions {
  /** Function to search for locations */
  onSearch: LocationSearchFunction
  /** Location types to search for */
  searchTypes?: LocationType[]
  /** Minimum characters before searching */
  minSearchLength?: number
  /** Maximum locations to display */
  maxLocations?: number
  /** Debounce delay in milliseconds */
  debounceDelay?: number
  /** Whether to enable map integration */
  enableMapIntegration?: boolean
  /** Map adapter options (e.g., marker placement callback) */
  mapAdapterOptions?: MapAdapterOptions
  /** Callback when location is selected or cleared */
  onChange?: (location: Location | null) => void
  /** Callback when location is selected (backward compatibility) */
  onLocationSelect?: (location: Location | null) => void
  /** Optional validation function */
  validateSelection?: LocationValidator
  /** Optional resolution function */
  resolveLocation?: LocationResolver
  /** Initial input value */
  initialValue?: string
}

/**
 * Return type for useLocationSearch hook
 */
export interface UseLocationSearchReturn {
  // Input state
  inputValue: string
  setInputValue: (value: string) => void

  // Search state
  locations: Location[]
  loading: boolean
  error: Error | null

  // Selection state
  selectedLocation: Location | null
  validationError: string | null
  isResolving: boolean

  // Dropdown state
  isOpen: boolean
  highlightedIndex: number

  // Actions
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleLocationSelect: (location: Location) => void
  handleClear: () => void
  openDropdown: () => void
  closeDropdown: () => void
  setHighlightedIndex: (index: number) => void

  // Refs (for UI integration)
  inputRef: React.RefObject<HTMLInputElement>
  containerRef: React.RefObject<HTMLDivElement>
  dropdownRef: React.RefObject<HTMLDivElement>
}

/**
 * Main headless hook for location search functionality
 * Provides complete search, selection, and dropdown management
 * Can be used to build custom search UIs
 *
 * @param options - Configuration options
 * @returns Complete search state and handlers
 */
export const useLocationSearch = ({
  onSearch,
  searchTypes = defaultSearchConfig.defaultSearchTypes,
  minSearchLength = defaultSearchConfig.minSearchLength,
  maxLocations = defaultSearchConfig.maxLocations,
  debounceDelay = defaultSearchConfig.debounceDelay,
  enableMapIntegration = true,
  mapAdapterOptions,
  onChange,
  onLocationSelect,
  validateSelection,
  resolveLocation,
  initialValue = '',
}: UseLocationSearchOptions): UseLocationSearchReturn => {
  // Input state
  const [inputValue, setInputValue] = useState(initialValue)

  // Search state
  const [locations, setLocations] = useState<Location[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  // Refs
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Get map adapter with options
  const mapAdapter = useMapAdapter(enableMapIntegration, mapAdapterOptions)

  // Dropdown state
  const dropdown = useDropdownState(false)

  // Location selection
  const selection = useLocationSelection({
    onChange,
    onLocationSelect,
    validateSelection,
    resolveLocation,
    mapAdapter,
  })

  // Search locations with debounce
  const searchLocations = useCallback(
    async (searchValue: string) => {
      if (searchValue.length < minSearchLength) {
        setLocations([])
        dropdown.close()
        return
      }

      setLoading(true)
      setError(null)
      dropdown.open()

      try {
        const results = await onSearch(searchValue, searchTypes)
        setLocations(results.slice(0, maxLocations))
      } catch (err) {
        console.error('Error searching locations:', err)
        setError(err as Error)
        setLocations([])
      } finally {
        setLoading(false)
      }
    },
    [minSearchLength, onSearch, searchTypes, maxLocations, dropdown]
  )

  // Handle input change with debounce
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setInputValue(newValue)
      setHighlightedIndex(-1)

      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      // If input is cleared, reset everything
      if (!newValue) {
        selection.handleClear()
        setLocations([])
        dropdown.close()
        return
      }

      // Set new timeout for search
      searchTimeoutRef.current = setTimeout(() => {
        searchLocations(newValue)
      }, debounceDelay)
    },
    [searchLocations, debounceDelay, selection, dropdown]
  )

  // Handle location selection
  const handleLocationSelect = useCallback(
    (location: Location) => {
      setInputValue(location.location_search)
      setHighlightedIndex(-1)
      dropdown.close()
      selection.handleSelect(location)
    },
    [selection, dropdown]
  )

  // Handle clear
  const handleClear = useCallback(() => {
    setInputValue('')
    setLocations([])
    setHighlightedIndex(-1)
    dropdown.close()
    selection.handleClear()
    inputRef.current?.focus()
  }, [selection, dropdown])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  return {
    // Input state
    inputValue,
    setInputValue,

    // Search state
    locations,
    loading,
    error,

    // Selection state
    selectedLocation: selection.selectedLocation,
    validationError: selection.validationError,
    isResolving: selection.isResolving,

    // Dropdown state
    isOpen: dropdown.isOpen,
    highlightedIndex,

    // Actions
    handleInputChange,
    handleLocationSelect,
    handleClear,
    openDropdown: dropdown.open,
    closeDropdown: dropdown.close,
    setHighlightedIndex,

    // Refs
    inputRef,
    containerRef,
    dropdownRef,
  }
}
